import { Component } from '@theme/component';
import {
  getScrollContainer,
  getScrollTop,
  getScrollEventTarget,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * A vertical scroll-progress indicator for the desktop PDP media gallery (Figma
 * 6084:43024): a thin track pinned at the viewport centre whose black fill grows
 * downward as the gallery is scrolled through ("Similar to Calibre" annotation).
 *
 * Fill = the fraction of the media grid that has entered the viewport, so it
 * starts partly filled (first image on screen) and reaches 100% when the last
 * image is fully scrolled into view. Desktop only (hidden via CSS < 750px).
 *
 * Scroll listening goes through @theme/scroll-container: Horizon scrolls
 * `.page-wrapper` in the squeeze layout (≥990px), not the window, and the
 * scroll event target flips at that breakpoint.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} thumb - The fill bar.
 *
 * @extends {Component<Refs>}
 */
class MediaScrollProgress extends Component {
  requiredRefs = ['thumb'];

  /** @type {HTMLElement | null} */
  #grid = null;
  /** @type {number} */
  #frame = 0;
  /** @type {EventTarget | null} */
  #scrollTarget = null;

  connectedCallback() {
    super.connectedCallback();
    const gallery = this.closest('media-gallery') ?? this.parentElement;
    this.#grid = gallery?.querySelector('.media-gallery__grid') ?? null;
    this.#bindScrollTarget();
    scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    window.addEventListener('resize', this.#handleScroll, { passive: true });
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#scrollTarget?.removeEventListener('scroll', this.#handleScroll);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    window.removeEventListener('resize', this.#handleScroll);
    cancelAnimationFrame(this.#frame);
  }

  #bindScrollTarget() {
    this.#scrollTarget?.removeEventListener('scroll', this.#handleScroll);
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#handleScroll, { passive: true });
  }

  // The scroll container flips between `.page-wrapper` and the document at 990px.
  #handleBreakpointChange = () => {
    this.#bindScrollTarget();
    this.#handleScroll();
  };

  // Arrow-function field so it's bound to `this` and stable for add/removeEventListener.
  #handleScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  // Track height = ~46.6% of a single image's height (Figma: 644 / 1382).
  static TRACK_HEIGHT_RATIO = 0.466;

  #update() {
    const grid = this.#grid;
    const { thumb } = this.refs;
    if (!grid || !thumb) return;

    const gridHeight = grid.offsetHeight;
    if (gridHeight <= 0) return;

    const firstItem = /** @type {HTMLElement | null} */ (grid.querySelector(':scope > .product-media-container'));
    if (firstItem && firstItem.offsetHeight > 0) {
      this.style.setProperty(
        '--media-scroll-progress-bar-height',
        `${firstItem.offsetHeight * MediaScrollProgress.TRACK_HEIGHT_RATIO}px`,
      );
    }

    // Document top of the grid via the offset chain — unaffected by the gallery's
    // position: sticky (getBoundingClientRect would report the pinned position).
    let gridTop = 0;
    for (let el = grid; el; el = /** @type {HTMLElement} */ (el.offsetParent)) {
      gridTop += el.offsetTop;
    }

    // Fraction of the grid that has entered the viewport: 0 while the grid is
    // still below the fold, 1 once its bottom reaches the viewport bottom.
    const viewportHeight = getScrollContainer().clientHeight;
    const entered = getScrollTop() + viewportHeight - gridTop;
    const progress = Math.max(0, Math.min(1, entered / gridHeight));

    thumb.style.height = `${progress * 100}%`;
  }
}

if (!customElements.get('media-scroll-progress')) {
  customElements.define('media-scroll-progress', MediaScrollProgress);
}
